import { useState } from "react";
import { Printer, ImageSquare, DownloadSimple, X, CopySimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { downloadBlob, blobError, openPdfForPrint, preparePrintWindow, printBlobInPage } from "../lib/download";
import { fmtBRL, fmtDate } from "../lib/format";
import { buildPlan, fmtISO } from "../lib/schedule";
import { StatusBadge } from "./StatusBadge";
import { TwoViasPrint } from "./TwoViasPrint";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const pLabel = "text-[11px] font-semibold uppercase tracking-wide text-blue-950 shrink-0";

const parcelSchedule = (doc) => {
  if (!doc.order_date || !(doc.total_value > 0)) return [];
  const plan = buildPlan({ total: doc.total_value, installments: doc.installments, payment_method: doc.payment_method,
    boleto_days: doc.boleto_days, order_date: doc.order_date, entrada_value: doc.entrada_value, due_dates: doc.installment_due_dates });
  const out = plan.parcelas.map((p) => ({ date: fmtISO(p.due_date), amount: p.amount }));
  if (plan.entrada > 0) out.unshift({ date: fmtISO(doc.order_date), amount: plan.entrada, entrada: true, method: doc.entrada_method || "PIX" });
  return out;
};

export const DocumentPreviewDialog = ({ doc, type, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [viasMode, setViasMode] = useState(false);
  if (!doc) return null;

  const isOrder = type === "order";
  const label = isOrder ? "PEDIDO" : "ORÇAMENTO";
  const pdfPath = isOrder ? `/orders/${doc.id}/pdf` : `/quotes/${doc.id}/pdf`;
  const items = (doc.items && doc.items.length > 0
    ? doc.items.filter((it) => !it.internal)
    : []).length > 0
    ? doc.items.filter((it) => !it.internal)
    : [{ quant: 1, description: doc.description || "Item", unit_price: doc.total_value }];
  const discount = doc.discount || 0;
  const freight = doc.freight_value || 0;
  const subtotal = Math.round(((doc.total_value || 0) + discount - freight) * 100) / 100;
  const parcels = parcelSchedule(doc);

  if (viasMode) {
    return (
      <TwoViasPrint doc={doc} items={items} discount={discount} freight={freight}
        subtotal={subtotal} onClose={() => setViasMode(false)} />
    );
  }

  const download = async (letterhead) => {
    setBusy(true);
    try {
      const res = await api.get(pdfPath, { params: { letterhead: letterhead ? 1 : 0 }, responseType: "blob" });
      downloadBlob(res.data, `${isOrder ? "pedido" : "orcamento"}_${doc.code}.pdf`);
      toast.success(`PDF do ${isOrder ? "pedido" : "orçamento"} #${doc.code} baixado`);
    } catch (err) {
      toast.error(await blobError(err, "Erro ao gerar o PDF"));
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    setBusy(true);
    const win = window.open("", "_blank");
    preparePrintWindow(win, "Preparando impressão — aguarde...");
    try {
      const res = await api.get(pdfPath, { params: { letterhead: 0 }, responseType: "blob" });
      if (win) {
        const url = URL.createObjectURL(res.data);
        openPdfForPrint(win, url, `${label} #${doc.code}`);
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } else {
        printBlobInPage(res.data, `${type === "quote" ? "orcamento" : "pedido"}_${doc.code}.pdf`);
        toast.info("Pop-up bloqueado pelo navegador — abrindo a impressão aqui mesmo. Permita pop-ups do site para abrir em nova aba.");
      }
    } catch (err) {
      if (win) win.close();
      toast.error(await blobError(err, "Erro ao preparar a impressão"));
    } finally {
      setBusy(false);
    }
  };

  const infoRow = (rowLabel, value, bg) => (
    <div className={`px-4 py-2 flex items-start gap-3 ${bg}`}>
      <span className={`${pLabel} w-24 pt-0.5`}>{rowLabel}</span>
      <span className="text-sm text-slate-900 flex-1 min-w-0 break-words">{value || "—"}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`${type}-preview-dialog`}
        className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-3xl max-h-[94dvh] sm:max-h-[92vh] overflow-y-auto p-3 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2">
            {isOrder ? "Pedido" : "Orçamento"} #{doc.code}
            <StatusBadge status={doc.status || (isOrder ? "Em processamento" : "Em aberto")} />
            {isOrder && <StatusBadge status={doc.payment_status || "Pendente"} />}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Prévia instantânea — nenhum arquivo é gerado até você clicar em um botão de ação.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid={`${type}-preview-actions`}>
          <button data-testid={`${type}-preview-print-button`} onClick={() => print()} disabled={busy}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
            <Printer size={16} /> Imprimir
          </button>
          {isOrder && (
            <button data-testid="order-preview-print-2vias-button" onClick={() => setViasMode(true)} disabled={busy}
              title="Mostra as 2 vias na tela (cliente + canhoto) para imprimir em uma única folha"
              className="flex items-center gap-2 bg-teal-700 hover:bg-teal-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
              <CopySimple size={16} /> Imprimir 2 vias
            </button>
          )}
          <button data-testid={`${type}-preview-pdf-button`} onClick={() => download(false)} disabled={busy}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
            <DownloadSimple size={16} /> Baixar PDF
          </button>
          <button data-testid={`${type}-preview-pdf-logo-button`} onClick={() => download(true)} disabled={busy}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors">
            <ImageSquare size={16} /> PDF com logotipo
          </button>
        </div>

        <div data-testid={`${type}-preview-slip`} style={{ colorScheme: "light" }}
          className="bg-slate-100 rounded-md overflow-hidden border border-slate-300 shadow-inner">
          <div className="bg-blue-950 text-white px-4 py-3 flex items-center justify-between gap-4">
            <span className="font-display font-semibold tracking-wide text-sm">
              {label} Nº{" "}
              <span data-testid={`${type}-preview-code`} className="bg-white/10 border border-white/20 rounded px-2 py-0.5 ml-1">#{doc.code}</span>
            </span>
            <span className="text-sm">
              <span className="text-[11px] font-semibold uppercase tracking-wide mr-2">Data</span>
              {fmtDate(doc.order_date)}
            </span>
          </div>

          <div className="divide-y divide-slate-300">
            {infoRow("Cliente", <span data-testid={`${type}-preview-client`} className="font-semibold">{doc.client_name}</span>, "bg-slate-200")}
            {infoRow("Endereço", doc.address, "bg-slate-100")}
            {infoRow("Cidade / Est.", [doc.city, doc.state].filter(Boolean).join(" / "), "bg-slate-200")}
            {infoRow("CPF / CNPJ", doc.document, "bg-slate-100")}
            {doc.notes && infoRow("Observações", doc.notes, "bg-slate-200")}
          </div>

          <div className="border-t border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-950 text-white text-[11px] uppercase tracking-wide">
                  <th className="px-3 py-2 font-semibold text-left w-16">Quant</th>
                  <th className="px-3 py-2 font-semibold text-left">Descrição / Produto</th>
                  <th className="px-3 py-2 font-semibold text-right w-28 whitespace-nowrap">Unitário</th>
                  <th className="px-3 py-2 font-semibold text-right w-28 whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody data-testid={`${type}-preview-items`}>
                {items.map((it, idx) => (
                  <tr key={`${it.description || "item"}-${it.unit_price}-${idx}`} className={idx % 2 === 0 ? "bg-slate-100" : "bg-slate-200"}>
                    <td className="px-3 py-2 text-slate-900">{it.quant}</td>
                    <td className="px-3 py-2 text-slate-900 break-words">{it.description || doc.description || "Item"}</td>
                    <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{fmtBRL(it.unit_price)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{fmtBRL((it.quant || 0) * (it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-slate-300 bg-slate-100 p-4 flex justify-end">
            <div className="flex flex-col items-stretch gap-1.5 w-full sm:w-auto sm:items-end">
              <div className="flex items-center justify-between gap-6 sm:justify-end">
                <span className={pLabel}>Subtotal</span>
                <span className="text-sm font-semibold text-slate-900 w-32 text-right">{fmtBRL(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between gap-6 sm:justify-end">
                  <span className={pLabel}>Desconto</span>
                  <span className="text-sm font-semibold text-rose-700 w-32 text-right">− {fmtBRL(discount)}</span>
                </div>
              )}
              {freight > 0 && (
                <div className="flex items-center justify-between gap-6 sm:justify-end">
                  <span className={pLabel}>Frete</span>
                  <span data-testid={`${type}-preview-freight`} className="text-sm font-semibold text-slate-900 w-32 text-right">+ {fmtBRL(freight)}</span>
                </div>
              )}
              {isOrder && doc.credit_applied_total > 0 && (
                <div className="flex items-center justify-between gap-6 sm:justify-end">
                  <span className={pLabel}>Crédito aplicado</span>
                  <span data-testid="order-preview-credit" className="text-sm font-semibold text-amber-700 w-32 text-right">− {fmtBRL(doc.credit_applied_total)}</span>
                </div>
              )}
              <div className="bg-blue-950 text-white rounded px-5 py-3 text-right w-full sm:w-auto mt-1">
                <p className="text-[10px] uppercase tracking-[0.15em] text-blue-200">Total Geral do {isOrder ? "Pedido" : "Orçamento"}</p>
                <p data-testid={`${type}-preview-total`} className="font-display text-2xl font-semibold">{fmtBRL(doc.total_value)}</p>
              </div>
              {isOrder && doc.credit_applied_total > 0 && (
                <div className="bg-emerald-700 text-white rounded px-5 py-2.5 text-right w-full sm:w-auto">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-200">Total a Pagar (desc. crédito)</p>
                  <p data-testid="order-preview-total-after-credit" className="font-display text-xl font-semibold">
                    {fmtBRL(doc.total_value - doc.credit_applied_total)}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-200 border-t border-slate-300 px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className={`${pLabel} block mb-0.5`}>Nº NF / Ped. Ext.</span>
              <span className="text-slate-900">{doc.nf_number || "—"}</span>
            </div>
            <div>
              <span className={`${pLabel} block mb-0.5`}>Forma de Pagamento</span>
              <span data-testid={`${type}-preview-payment-method`} className="text-slate-900">
                {doc.payment_method}
                {doc.payment_method === "Boleto" && doc.boleto_days > 0 && ` (${doc.boleto_days} dias)`}
              </span>
            </div>
            <div>
              <span className={`${pLabel} block mb-0.5`}>Parcelas</span>
              <span className="text-slate-900">{doc.installments}x{doc.entrada_value > 0 && <span className="text-amber-700"> + entrada {fmtBRL(doc.entrada_value)} ({doc.entrada_method || "PIX"})</span>}</span>
            </div>
            {isOrder ? (
              <div>
                <span className={`${pLabel} block mb-0.5`}>Comissão</span>
                <span className="text-slate-900">{doc.commission_percent > 0 ? `${String(doc.commission_percent).replace(".", ",")}%` : "Sem comissão"}</span>
              </div>
            ) : (
              <div>
                <span className={`${pLabel} block mb-0.5`}>Validade</span>
                <span className="text-slate-900">{doc.validity_days > 0 ? `${doc.validity_days} dias` : "—"}</span>
              </div>
            )}
          </div>

          {parcels.length > 0 && (
            <div className="bg-slate-100 border-t border-slate-300 px-4 py-3">
              <span className={pLabel}>{isOrder ? "Vencimentos das Parcelas" : "Previsão de Parcelas"}</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {parcels.map((p, i) => (
                  <span key={`${p.date}-${i}`} className={`text-xs border rounded px-2.5 py-1 ${p.entrada ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-slate-300 text-slate-800"}`}>
                    <span className="font-semibold text-blue-950">{p.entrada ? "Entrada" : `${i + (parcels[0]?.entrada ? 0 : 1)}ª`}</span> · {p.date} · {fmtBRL(p.amount)}{p.entrada && ` · paga (${p.method})`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {isOrder && doc.has_signature && (
            <div className="bg-slate-200 border-t border-slate-300 px-4 py-2.5 text-xs text-emerald-700 font-semibold">
              ✓ Este pedido possui assinatura de recebimento (visível no PDF)
            </div>
          )}
        </div>

        <button data-testid={`${type}-preview-close-button`} onClick={onClose}
          className="w-full flex items-center justify-center gap-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm font-medium py-2.5 rounded transition-colors">
          <X size={14} /> Fechar
        </button>
      </DialogContent>
    </Dialog>
  );
};
